import { Injectable, inject } from '@angular/core';
import { from, map, Observable } from 'rxjs';
import {
  AssetCreateRequest,
  AssetDetails,
  AssetsApi,
  AssetUpdateRequest,
  HttpDataAddress,
  HttpDataAuth,
  HttpDataAuthMethodEnum,
  IdResponse,
  SecretValue,
  SecretValueTypeEnum
} from '@adesso/dataspace-api-client';
import { ADESSO_API_CONFIG } from '../../../../core/api/adesso-api.config';
import {
  Asset,
  AssetCreatedInfo,
  AssetDeletedInfo,
  AssetUpdatedInfo,
  AuthMethod,
  QueryParam,
  QUERY_PARAM_MASK_KEY
} from '../models/asset.model';
import { AssetApiPort } from '../ports/asset-api.port';

type PropertyBag = Record<string, object>;

@Injectable()
export class AdessoAssetApiAdapter extends AssetApiPort {
  private readonly api = new AssetsApi(inject(ADESSO_API_CONFIG));

  getAssets(): Observable<Asset[]> {
    return from(this.api.getAllAssets()).pipe(
      map((res) => (res.data ?? []).map(toDomain))
    );
  }

  createAsset(asset: Asset): Observable<AssetCreatedInfo> {
    return from(this.api.createAsset(toCreateRequest(asset))).pipe(
      map((res) => toCreatedInfo(res.data))
    );
  }

  updateAsset(asset: Asset): Observable<AssetUpdatedInfo> {
    if (!asset.id) {
      throw new Error('AssetApiPort.updateAsset requires an asset.id');
    }
    return from(this.api.updateAsset(asset.id, toUpdateRequest(asset))).pipe(
      map(() => ({ updated: true }))
    );
  }

  deleteAsset(id: string): Observable<AssetDeletedInfo> {
    return from(this.api.removeAsset(id)).pipe(map(() => ({ deleted: true })));
  }
}

function toCreateRequest(asset: Asset): AssetCreateRequest {
  return {
    assetId: asset.id ?? '',
    properties: toProperties(asset),
    privateProperties: buildPrivateProperties(asset),
    dataAddress: toDataAddress(asset)
  };
}

function toUpdateRequest(asset: Asset): AssetUpdateRequest {
  return {
    properties: toProperties(asset),
    privateProperties: buildPrivateProperties(asset),
    dataAddress: toDataAddress(asset)
  };
}

/**
 * Merges the app-only "which query params are masked" metadata into privateProperties.
 * Masking is a display concern (values stay cleartext in the DataAddress); persisting
 * the masked-key list here (provider-private, not catalog-visible) lets the app restore
 * the masked state on reload.
 */
function buildPrivateProperties(asset: Asset): PropertyBag {
  const base = { ...(asset.privateProperties ?? {}) } as Record<string, unknown>;
  const maskedKeys = (asset.dataAddress.queryParams ?? [])
    .filter(p => p.masked)
    .map(p => p.key);
  if (maskedKeys.length) {
    base[QUERY_PARAM_MASK_KEY] = maskedKeys;
  } else {
    delete base[QUERY_PARAM_MASK_KEY];
  }
  return base as PropertyBag;
}

function extractMaskedKeys(priv: unknown): Set<string> {
  const bag = priv as Record<string, unknown> | null | undefined;
  const raw = bag?.[QUERY_PARAM_MASK_KEY];
  return Array.isArray(raw) ? new Set(raw.map(String)) : new Set();
}

function toProperties(asset: Asset): PropertyBag {
  const p = asset.properties;
  return {
    name: p.name,
    description: p.description,
    contenttype: p.contenttype ?? 'application/json',
    publisher: p.publisher,
    sovereign: p.sovereign,
    dataOfferSource: p.dataOfferSource,
    keywords: p.keywords,
    subCategory: p.subCategory,
    serviceType: p.serviceType,
    type: p.type,
    language: p.language,
    datasource: p.datasource,
    method: p.method,
    serviceOfferName: p.serviceOfferName,
    baseUrl: p.baseUrl
  } as unknown as PropertyBag;
}

function toDataAddress(asset: Asset): HttpDataAddress {
  const a = asset.dataAddress;
  const address: HttpDataAddress = {
    type: a.type || 'HttpData',
    baseUrl: a.baseUrl,
    proxyPath: a.proxyPath,
    proxyQueryParams: a.proxyQueryParams,
    proxyMethod: a.proxyMethod,
    proxyBody: a.proxyBody,
    auth: toAuth(a)
  };
  const queryParams = serializeQueryParams(a.queryParams);
  if (queryParams) {
    address.queryParams = queryParams;
  }
  return address;
}

/** Domain key/value pairs → the flat EDC wire form ("k=v&k2=v2", URL-encoded). */
function serializeQueryParams(params: QueryParam[] | undefined): string | undefined {
  if (!params?.length) return undefined;
  const serialized = params
    .filter(p => p.key.trim())
    .map(p => `${encodeURIComponent(p.key.trim())}=${encodeURIComponent(p.value ?? '')}`)
    .join('&');
  return serialized || undefined;
}

/** Flat EDC wire form → domain key/value pairs (inverse of serializeQueryParams). */
function parseQueryParams(raw: unknown, maskedKeys: Set<string>): QueryParam[] | undefined {
  if (typeof raw !== 'string' || !raw.trim()) return undefined;
  const params = raw
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      const key = safeDecode(eq >= 0 ? pair.slice(0, eq) : pair);
      const value = safeDecode(eq >= 0 ? pair.slice(eq + 1) : '');
      return { key, value, masked: maskedKeys.has(key) || undefined };
    })
    .filter((p) => p.key);
  return params.length ? params : undefined;
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function toAuth(da: Asset['dataAddress']): HttpDataAuth {
  switch (da.authMethod) {
    case AuthMethod.API_KEY:
      return {
        method: HttpDataAuthMethodEnum.API_KEY,
        headerName: da.authHeaderName ?? '',
        value: literalSecret(da.authHeaderValue ?? '')
      };
    case AuthMethod.NONE:
      return { method: HttpDataAuthMethodEnum.NONE };
    default:
      throw new Error(
        `Unsupported auth method '${da.authMethod}'. Spec 1.0.7 supports NONE and API_KEY only.`
      );
  }
}

function literalSecret(value: string): SecretValue {
  return { type: SecretValueTypeEnum.LITERAL, value };
}

function toDomain(dto: AssetDetails): Asset {
  const props = (dto.properties ?? {}) as Record<string, unknown>;
  const da = dto.dataAddress;
  const maskedKeys = extractMaskedKeys(dto.privateProperties);
  return {
    id: dto.assetId ?? '',
    properties: {
      name: str(props['name']),
      description: optionalStr(props['description']),
      language: optionalStr(props['language']),
      publisher: optionalStr(props['publisher']),
      sovereign: optionalStr(props['sovereign']),
      dataOfferSource: optionalStr(props['dataOfferSource']),
      datasource: optionalStr(props['datasource']),
      keywords: normalizeKeywords(props['keywords']),
      subCategory: optionalStr(props['subCategory']),
      serviceType: optionalStr(props['serviceType']),
      type: optionalStr(props['type']),
      method: optionalStr(props['method']),
      serviceOfferName: optionalStr(props['serviceOfferName']),
      baseUrl: optionalStr(props['baseUrl']),
      contenttype: optionalStr(props['contenttype'])
    },
    dataAddress: {
      type: da?.type || 'HttpData',
      baseUrl: da?.baseUrl ?? '',
      proxyPath: da?.proxyPath ?? false,
      proxyQueryParams: da?.proxyQueryParams ?? false,
      proxyMethod: da?.proxyMethod ?? false,
      proxyBody: da?.proxyBody ?? false,
      queryParams: parseQueryParams(da?.queryParams, maskedKeys),
      ...fromAuth(da?.auth)
    },
    privateProperties: (dto.privateProperties ?? undefined) as Record<string, unknown> | undefined
  };
}

function fromAuth(auth: HttpDataAuth | undefined): Pick<Asset['dataAddress'], 'authMethod' | 'authHeaderName' | 'authHeaderValue'> {
  if (!auth || auth.method === HttpDataAuthMethodEnum.NONE) {
    return { authMethod: AuthMethod.NONE };
  }
  if (auth.method === HttpDataAuthMethodEnum.API_KEY) {
    return {
      authMethod: AuthMethod.API_KEY,
      authHeaderName: auth.headerName ?? '',
      authHeaderValue: auth.value?.type === SecretValueTypeEnum.LITERAL ? (auth.value.value ?? '') : ''
    };
  }
  return { authMethod: AuthMethod.NONE };
}

function normalizeKeywords(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((k: unknown) => String(k).trim()).filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function str(v: unknown): string {
  return v == null ? '' : String(v);
}

function optionalStr(v: unknown): string | undefined {
  return v == null ? undefined : String(v);
}

function toCreatedInfo(resp: IdResponse): AssetCreatedInfo {
  return {
    id: resp.id ?? '',
    type: 'Asset',
    createdAt: resp.createdAt ? new Date(resp.createdAt) : new Date()
  };
}
